/**
 * Global "Talk to a Concierge® on WhatsApp" pill.
 *
 * Founder ruling 18 Feb 2026 — present on every member surface
 * (/escape, /enjoy, /table, /club, future modules). Sticky-bottom-
 * right · safe-area aware · 44px tap-target · WhatsApp brand green.
 *
 * Originally lived inside Table.jsx as `TableWhatsAppPill` · pulled
 * out as a shared component so every surface uses the SAME pill
 * (no copy-paste drift). The Table.jsx wrapper still re-exports it
 * under the old name for backwards compatibility.
 *
 * Phone number sourced from REACT_APP_LC_WHATSAPP_NUMBER (preview /
 * prod can override). Fallback is the live Gupshup source number.
 */
import React from "react";

const FALLBACK = "918792622424"; // GUPSHUP_SOURCE_NUMBER
const NUMBER =
    (typeof process !== "undefined"
        && process?.env?.REACT_APP_LC_WHATSAPP_NUMBER)
    || FALLBACK;

function buildWaLink(prefillText) {
    const t = prefillText
        || "Hi Concierge®, I'd like to talk to a human.";
    return `https://wa.me/${NUMBER}?text=${encodeURIComponent(t)}`;
}

const SURFACE_PREFILLS = {
    escape: "Hi Concierge®, I'm exploring Escape — could you arrange this for me?",
    enjoy: "Hi Concierge®, I'd love a hand with Enjoy.",
    table: "Hi Concierge®, I'm choosing a table — could you help?",
    club: "Hi Concierge®, I'd like to chat about Club Social.",
    curate: "Hi Concierge®, I'm in the studio — quick question.",
};

export default function ConciergeWhatsAppPill({ context = "general" }) {
    const onClick = (e) => {
        e.preventDefault();
        const here = (typeof window !== "undefined" && window.location?.href) || "";
        const prefill =
            SURFACE_PREFILLS[context]
            || `Hi Concierge®, I'm browsing ${here} — I'd love a hand.`;
        if (typeof window !== "undefined") {
            window.open(buildWaLink(prefill), "_blank", "noopener,noreferrer");
        }
    };
    return (
        <a
            href={buildWaLink()}
            onClick={onClick}
            data-testid={`concierge-whatsapp-pill-${context}`}
            className="lc-float-pill"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Talk to a Concierge® on WhatsApp"
            style={{
                position: "fixed",
                right: "max(18px, env(safe-area-inset-right, 0px))",
                bottom: "calc(max(18px, env(safe-area-inset-bottom, 0px)) + 8px)",
                zIndex: 60,
                display: "inline-flex",
                alignItems: "center",
                gap: 9,
                padding: "12px 18px",
                borderRadius: 999,
                background: "#0a7e3e",
                color: "#fff",
                fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.02em",
                textDecoration: "none",
                boxShadow: "0 12px 32px rgba(33,28,21,0.18)",
                border: "1.5px solid rgba(255,255,255,0.18)",
                minHeight: 44,
            }}
        >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                    d="M12 2C6.48 2 2 6.48 2 12c0 1.74.45 3.45 1.3 4.95L2 22l5.2-1.27A9.95 9.95 0 0 0 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm5.5 14.05c-.23.65-1.36 1.24-1.88 1.32-.5.08-1.16.12-1.86-.12-.43-.13-.99-.32-1.7-.63-2.99-1.29-4.93-4.3-5.08-4.5-.15-.2-1.21-1.61-1.21-3.07s.77-2.18 1.04-2.48c.27-.3.59-.37.78-.37l.56.01c.18 0 .42-.07.66.5.23.56.79 1.96.86 2.1.07.13.12.29.02.47-.1.18-.15.29-.29.45-.14.16-.3.36-.43.48-.14.13-.3.27-.13.55.17.28.76 1.26 1.65 2.04 1.14 1.01 2.1 1.32 2.38 1.46.28.13.45.11.62-.07.18-.18.71-.84.9-1.13.18-.28.36-.23.62-.13.26.1 1.62.77 1.9.91.28.13.46.2.53.32.07.13.07.71-.16 1.36z"
                    fill="currentColor"
                />
            </svg>
            <span className="lc-float-pill-label">
                Talk to a Concierge
                <sup
                    style={{
                        fontSize: "0.55em",
                        marginLeft: "1px",
                        fontWeight: "normal",
                        verticalAlign: "super",
                        lineHeight: 0,
                    }}
                    aria-label="registered trademark"
                >
                    ®
                </sup>
            </span>
        </a>
    );
}
